import { Controller } from "@hotwired/stimulus"
import { onHeaderSignup } from "../lib/header_signup"

// Docs tab: the template library is the page. A card opens a preview modal where the document
// takes on the visitor's company name, colour and logo; the email is asked for only on send.
// Everything is local: the logo never leaves the browser and nothing is saved before sign-up.
const MAX_LOGO_BYTES = 2 * 1024 * 1024
const MAX_INITIALS_SHOWN = 3
const INITIAL_COLOURS = ['#2f9e6e', '#3b6fd6', '#e0813a', '#9a5bd6', '#d9488e']
const PLACEHOLDER_COMPANY = 'Your company name'
const COMPANY_PLACEHOLDER = /\[?your company name\]?/gi
const PAGE_SIZE_FALLBACK = 8
const SORT_USES = 'uses'
const SORT_NAME = 'name'
const KIND_LABELS = { Invoices: 'Invoice', Contracts: 'Contract', 'Health and safety': 'Health and safety', Terms: 'Terms', Site: 'Site form', Other: 'Document' }
const SIGNATURE_FIELD = 'signature'
const RICH_FIELD = 'rich_textarea'
const PDF_FIELD = 'pdf'
const UNSAFE_TAGS = 'script, iframe, object, embed, link, style, form'

export default class extends Controller {
  static targets = [
    "search", "pill", "pillCount", "showing", "grid", "more", "proofInitials", "sort",
    "preview", "previewTitle", "previewKind", "previewBadge", "previewCounter", "previewCounterInline", "previewProof", "prev", "next",
    "company", "swatch", "logoBtn", "logoInput", "send",
    "paperWrap", "paperLogo", "paperCompany", "paperRef", "paperDate", "paperTitle", "paperBody", "signedFor"
  ]
  static values = { pageSize: Number, templates: Array, sendCopy: Object, minUses: Number, colour: String, signedIn: Boolean, appUrl: String }

  connect() {
    this.templates = [...this.templatesValue]
    this.activeFilter = 'All'
    this.sortBy = SORT_USES
    this.query = ''
    this.expanded = false
    this.shown = []
    this.colour = this.colourValue
    this.logo = null
    this.element.style.setProperty('--dl-colour', this.colour)

    this.renderProof()
    this.renderPillCounts()
    this.renderLibrary()

    this.onKeydown = (event) => { if (event.key === 'Escape' && !this.previewTarget.hidden && !this.signupOpen()) this.closePreview() }
    document.addEventListener('keydown', this.onKeydown)
    // The header's "Sign up free" button opens this modal while the Docs tab is showing
    this.stopHeaderSignup = onHeaderSignup(this.element, () => this.openSignup())
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKeydown)
    this.stopHeaderSignup()
  }

  // ── Library ──

  filter(event) {
    this.query = event.currentTarget.value
    this.expanded = false
    this.renderLibrary()
  }

  pickFilter(event) {
    this.activeFilter = event.currentTarget.dataset.filter
    this.pillTargets.forEach(pill => {
      const on = pill.dataset.filter === this.activeFilter
      pill.classList.toggle('dl__pill--on', on)
      pill.setAttribute('aria-pressed', String(on))
    })
    this.expanded = false
    this.renderLibrary()
  }

  toggleSort() {
    this.sortBy = this.sortBy === SORT_USES ? SORT_NAME : SORT_USES
    this.sortTarget.textContent = this.sortBy === SORT_USES ? 'Most used ▾' : 'A to Z ▾'
    this.renderLibrary()
  }

  showMore() {
    this.expanded = true
    this.renderLibrary()
  }

  get visibleTemplates() {
    const query = this.query.trim().toLowerCase()
    const matching = this.templates.filter(tpl =>
      (this.activeFilter === 'All' || tpl.kind === this.activeFilter) &&
      (!query || `${tpl.name} ${tpl.description || ''} ${tpl.category}`.toLowerCase().includes(query))
    )
    return this.sortBy === SORT_USES ? matching : matching.sort((a, b) => a.name.localeCompare(b.name))
  }

  // Counts beside the pills: "All 42", "Contracts 3"
  renderPillCounts() {
    this.pillCountTargets.forEach(el => {
      const filter = el.dataset.filter
      const count = filter === 'All' ? this.templates.length : this.templates.filter(tpl => tpl.kind === filter).length
      el.textContent = filter === 'All' && count >= 50 ? '50+' : String(count)
    })
  }

  renderProof() {
    const initials = [...new Set(this.templates.flatMap(tpl => tpl.social_proof_initials))].slice(0, 5)
    this.proofInitialsTarget.replaceChildren(...initials.map((letters, index) => this.initialDot(letters, index)))
  }

  renderLibrary() {
    const visible = this.visibleTemplates
    const pageSize = this.pageSizeValue || PAGE_SIZE_FALLBACK
    this.shown = this.expanded ? visible : visible.slice(0, pageSize)
    const hidden = visible.length - this.shown.length
    const total = this.templates.length >= 50 ? '50+' : this.templates.length

    this.gridTarget.replaceChildren(...this.shown.map(tpl => this.buildTile(tpl)))
    if (!visible.length) this.gridTarget.append(this.el('p', 'dl__empty', 'No documents match that search.'))
    this.showingTarget.textContent = `Showing ${this.shown.length} of ${total} documents`
    this.moreTarget.hidden = hidden === 0
    this.moreTarget.textContent = `Show ${hidden} more documents`
  }

  // A card: big thumbnail, author badge, title, social proof and a preview button
  buildTile(tpl) {
    const tile = this.el('div', 'dl-tile')
    tile.addEventListener('click', () => this.openPreview(tpl.id))

    const thumb = this.el('div', 'dl-tile__thumb')
    thumb.append(this.el('span', 'dl-tile__doc'), this.authorBadge(tpl))

    const body = this.el('div', 'dl-tile__body')
    body.append(this.el('strong', 'dl-tile__title', tpl.name), this.el('small', 'dl-card__meta', `${KIND_LABELS[tpl.kind]} · PDF`))
    const proof = this.socialProof(tpl)
    if (proof) body.append(proof)

    const cta = this.el('button', 'dl-tile__cta')
    cta.type = 'button'
    cta.append(this.el('span', 'dl-tile__cta-text', 'Preview and personalise'), this.el('i', 'bi bi-arrow-right'))
    body.append(cta)

    tile.append(thumb, body)
    return tile
  }

  authorBadge(tpl) {
    return this.el('span', `dl-card__author${tpl.community ? ' dl-card__author--community' : ''}`, tpl.community ? 'Community' : 'GoodEvent')
  }

  initialDot(letters, index) {
    const dot = this.el('span', 'dl-card__initial', letters.slice(0, 2))
    dot.style.background = INITIAL_COLOURS[index % INITIAL_COLOURS.length]
    return dot
  }

  // Other companies' initials and a uses count: shown only once a document has been used.
  socialProof(tpl) {
    if (tpl.uses < this.minUsesValue && !tpl.social_proof_initials.length) return null

    const row = this.el('span', 'dl-card__proof')
    const initials = this.el('span', 'dl-card__initials')
    tpl.social_proof_initials.slice(0, MAX_INITIALS_SHOWN).forEach((letters, index) => initials.append(this.initialDot(letters, index)))
    row.append(initials)
    if (tpl.uses >= this.minUsesValue) row.append(this.el('small', 'dl-card__uses', `${tpl.uses.toLocaleString('en-GB')} ${tpl.uses === 1 ? 'use' : 'uses'}`))
    return row
  }

  // ── Preview modal ──

  openPreview(id) {
    this.selectedId = id
    this.previewTarget.hidden = false
    this.renderPreview()
    this.paperWrapTarget.scrollTop = 0
    this.previewTarget.querySelector('.dl-preview__round--close').focus()
  }

  closePreview() {
    this.previewTarget.hidden = true
  }

  previous() {
    this.step(-1)
  }

  next() {
    this.step(1)
  }

  // Step through the documents on the page without closing the preview
  step(direction) {
    const index = this.shown.findIndex(tpl => tpl.id === this.selectedId) + direction
    if (index < 0 || index >= this.shown.length) return

    this.selectedId = this.shown[index].id
    this.renderPreview()
    this.paperWrapTarget.scrollTop = 0
  }

  get selected() {
    return this.templates.find(tpl => tpl.id === this.selectedId)
  }

  renderPreview() {
    const tpl = this.selected
    const position = this.shown.findIndex(item => item.id === tpl.id) + 1
    const counter = `${position} of ${this.shown.length}`

    this.previewTitleTarget.textContent = tpl.name
    this.previewKindTarget.textContent = `${KIND_LABELS[tpl.kind]} · PDF`
    this.previewBadgeTarget.textContent = tpl.community ? 'Community' : 'GoodEvent'
    this.previewBadgeTarget.classList.toggle('dl-preview__badge--community', tpl.community)
    this.previewCounterTarget.textContent = counter
    this.previewCounterInlineTarget.textContent = ` · ${counter}`
    this.prevTarget.disabled = position === 1
    this.nextTarget.disabled = position === this.shown.length
    this.sendTarget.textContent = this.sendCopyFor(tpl).button
    this.previewProofTarget.replaceChildren(...[this.socialProof(tpl)].filter(Boolean))
    this.renderPaper()
  }

  // ── Personalise ──

  personalise() {
    this.renderPaper()
  }

  pickColour(event) {
    this.colour = event.currentTarget.dataset.colour
    this.element.style.setProperty('--dl-colour', this.colour)
    this.swatchTargets.forEach(swatch => {
      const on = swatch === event.currentTarget
      swatch.classList.toggle('dl__swatch--on', on)
      swatch.setAttribute('aria-pressed', String(on))
    })
  }

  chooseLogo() {
    this.logoInputTarget.click()
  }

  logoChosen() {
    const file = this.logoInputTarget.files[0]
    if (!file) return

    if (!file.type.startsWith('image/') || file.size > MAX_LOGO_BYTES) {
      this.logoBtnTarget.textContent = 'Image under 2 MB'
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      this.logo = reader.result
      this.logoBtnTarget.textContent = 'Logo added'
      this.logoBtnTarget.classList.add('dl__logo--on')
      this.renderPaper()
    }
    reader.readAsDataURL(file)
  }

  get companyName() {
    return this.companyTarget.value.trim() || PLACEHOLDER_COMPANY
  }

  withCompany(text) {
    return text.replace(COMPANY_PLACEHOLDER, this.companyName)
  }

  // ── Paper ──

  renderPaper() {
    const tpl = this.selected
    if (!tpl) return

    const company = this.companyName
    this.paperLogoTarget.replaceChildren(this.logo ? this.logoImage() : document.createTextNode(company.charAt(0).toUpperCase()))
    this.paperLogoTarget.classList.toggle('dl__paper-logo--image', Boolean(this.logo))
    this.paperCompanyTarget.textContent = company
    this.paperRefTarget.textContent = `Ref ${this.reference(tpl)}`
    this.paperDateTarget.textContent = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    this.paperTitleTarget.textContent = this.withCompany(tpl.name)
    this.signedForTarget.textContent = `Signed for ${company}`
    this.paperBodyTarget.replaceChildren(...this.clauses(tpl))
  }

  logoImage() {
    const img = this.el('img', 'dl__paper-logo-img')
    img.src = this.logo
    img.alt = ''
    return img
  }

  reference(tpl) {
    const letters = tpl.name.split(/\W+/).filter(Boolean).map(word => word[0]).join('').toUpperCase().slice(0, 3)
    return `${letters || 'DOC'}-1042`
  }

  clauses(tpl) {
    const nodes = []
    if (tpl.description) nodes.push(this.el('p', 'dl__paper-intro', this.withCompany(tpl.description)))

    tpl.sections.forEach((section, index) => {
      const fields = section.fields.filter(field => field.type !== SIGNATURE_FIELD)
      if (!fields.some(field => field.content || (field.label || '').trim() || field.type === PDF_FIELD)) return

      const clause = this.el('section', 'dl__clause')
      clause.append(this.el('h3', 'dl__clause-title', `${index + 1}. ${this.withCompany(section.title)}`))
      fields.forEach(field => { const row = this.clauseField(field); if (row) clause.append(row) })
      nodes.push(clause)
    })
    return nodes
  }

  clauseField(field) {
    if (field.type === RICH_FIELD && field.content) {
      const rich = this.el('div', 'dl__clause-rich')
      rich.append(this.cleanHtml(this.withCompany(field.content)))
      return rich
    }
    if (field.type === PDF_FIELD) return this.el('p', 'dl__clause-text', `Attached: ${field.pdf_filename || 'document'}`)
    if (!(field.label || '').trim()) return null

    const row = this.el('div', 'dl__clause-field')
    row.append(this.el('span', 'dl__clause-label', this.withCompany(field.label)), this.el('span', 'dl__clause-line'))
    return row
  }

  // Template text is written by GoodEvent staff, but it is still parsed into inert nodes
  // with anything executable removed rather than assigned straight to innerHTML.
  cleanHtml(html) {
    const template = document.createElement('template')
    template.innerHTML = html
    template.content.querySelectorAll(UNSAFE_TAGS).forEach(node => node.remove())
    template.content.querySelectorAll('*').forEach(node => {
      [...node.attributes].filter(attr => /^on/i.test(attr.name) || /^\s*javascript:/i.test(attr.value)).forEach(attr => node.removeAttribute(attr.name))
    })
    return template.content
  }

  sendCopyFor(tpl) {
    return this.sendCopyValue[tpl.kind] || this.sendCopyValue.default
  }

  // ── Send: the email ask ──

  signupModal() {
    return this.element.querySelector('.signup-modal')
  }

  signupOpen() {
    return this.signupModal().style.display === 'flex'
  }

  openSignup() {
    // A signed-in visitor already has an account: send them to their documents
    if (this.signedInValue) {
      window.location.href = this.appUrlValue
      return
    }

    const modal = this.signupModal()
    const controller = this.application.getControllerForElementAndIdentifier(modal, 'signup-modal')
    const picked = !this.previewTarget.hidden
    // Hand over the template they picked; on confirmation it is cloned into their library
    controller.templateGuidValue = picked ? this.selectedId || '' : ''
    modal.querySelector('.signup-modal__subtitle').textContent = picked
      ? `Enter your email and we'll ${this.sendCopyFor(this.selected).action}, and save it to your free account. No password needed.`
      : "Enter your email and we'll send a link to your free account, with every document ready to personalise. No password needed."
    modal.querySelector('.signup-modal__title').textContent = picked ? 'One step to send this' : 'Start free with GoodEvent Docs'
    controller.open()
  }

  el(tag, className, text) {
    const node = document.createElement(tag)
    if (className) node.className = className
    if (text !== undefined) node.textContent = text
    return node
  }
}
